/**
 * AI Provider Factory and Exports
 * 
 * This module provides the factory function for creating AI providers
 * and exports all provider-related types and utilities.
 */

import {
  AIProvider,
  AIProviderType,
  AIProviderConfig,
  PROVIDER_ENV_KEYS,
  PROVIDER_DEFAULT_MODELS,
} from './types';
import { OpenAIProvider } from './openai-provider';
import { GoogleProvider } from './google-provider';

// Re-export types
export type {
  AIProvider,
  AIProviderType,
  AIProviderConfig,
  ChatMessage,
  ChatCompletionOptions,
  ChatCompletionResponse,
} from './types';

export { PROVIDER_ENV_KEYS, PROVIDER_DEFAULT_MODELS } from './types';

/**
 * Creates an AI provider instance based on the specified provider type
 * 
 * @param providerType - The type of provider to create ('openai' | 'google')
 * @param apiKey - The API key for the provider
 * @param config - Configuration options (model, temperature, maxTokens)
 * @returns An AIProvider instance
 * @throws Error if the provider type is not supported
 * 
 * @example
 * ```typescript
 * const provider = createAIProvider('openai', process.env.OPENAI_API_KEY, {
 *   model: 'gpt-4o-mini',
 *   temperature: 0.1,
 *   maxTokens: 1000,
 * });
 * 
 * const response = await provider.chat([
 *   { role: 'system', content: 'You are a helpful assistant.' },
 *   { role: 'user', content: 'Hello!' },
 * ], { jsonMode: true });
 * ```
 */
export function createAIProvider(
  providerType: AIProviderType,
  apiKey: string,
  config: AIProviderConfig
): AIProvider {
  switch (providerType) {
    case 'openai':
      return new OpenAIProvider(apiKey, config);
    case 'google':
      return new GoogleProvider(apiKey, config);
    default:
      throw new Error(`Unsupported AI provider: ${providerType}. Supported providers: openai, google`);
  }
}

/**
 * Gets the environment variable name for a provider's API key
 * 
 * @param providerType - The provider type
 * @returns The environment variable name
 */
export function getProviderEnvKey(providerType: AIProviderType): string {
  return PROVIDER_ENV_KEYS[providerType];
}

/**
 * Gets the API key for a provider from environment variables
 * 
 * @param providerType - The provider type
 * @returns The API key or empty string if not set
 */
export function getProviderApiKey(providerType: AIProviderType): string {
  const envKey = getProviderEnvKey(providerType);
  return process.env[envKey] || '';
}

/**
 * Gets the default model for a provider
 * 
 * @param providerType - The provider type
 * @returns The default model name
 */
export function getProviderDefaultModel(providerType: AIProviderType): string {
  return PROVIDER_DEFAULT_MODELS[providerType];
}

/**
 * Validates that a provider is supported
 * 
 * @param providerType - The provider type to validate
 * @returns true if supported, false otherwise
 */
export function isValidProvider(providerType: string): providerType is AIProviderType {
  return providerType === 'openai' || providerType === 'google';
}

